import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, DollarSign, TrendingUp, Users, ShoppingCart, 
  Copy, ExternalLink, Clock, CheckCircle, Wallet, 
  CreditCard, Building, Mail, Loader2, AlertCircle,
  Eye, Calendar, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import authService from '../services/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReferralDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  
  // Payment method state
  const [showPaymentSetup, setShowPaymentSetup] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('paypal');
  const [paymentDetails, setPaymentDetails] = useState({
    paypal_email: '',
    venmo_handle: '',
    bank_name: '',
    bank_account_name: '',
    bank_routing: '',
    bank_account: ''
  });
  const [savingPayment, setSavingPayment] = useState(false);

  useEffect(() => {
    checkAuthAndLoadDashboard();
  }, []);

  const checkAuthAndLoadDashboard = async () => {
    try {
      // Check if user is logged in
      const currentUser = await authService.getCurrentUser();
      
      if (!currentUser) {
        // Not logged in - redirect to login
        toast.error('Please log in to access your referral dashboard');
        authService.login('/referral-dashboard');
        return;
      }
      
      setUser(currentUser);
      
      // Load dashboard data
      await loadDashboard(currentUser.email);
    } catch (err) {
      console.error('Auth check failed:', err);
      setError('Failed to authenticate. Please try logging in again.');
    } finally {
      setLoading(false);
    }
  };

  const loadDashboard = async (email) => {
    try {
      const response = await fetch(`${API_URL}/api/referrals/dashboard/${encodeURIComponent(email)}`);
      
      if (response.status === 404) {
        setError('no_account');
        return;
      }
      
      if (!response.ok) {
        throw new Error('Failed to load dashboard');
      }
      
      const data = await response.json();
      setDashboardData(data);
      
      // Load saved payment method if exists
      if (data.referrer?.payment_method) {
        setPaymentMethod(data.referrer.payment_method);
        setPaymentDetails(data.referrer.payment_details || {});
      }
    } catch (err) {
      console.error('Dashboard load error:', err);
      setError('Failed to load dashboard data');
    }
  };

  const handleCopyLink = () => {
    if (dashboardData?.referrer?.referral_link) {
      navigator.clipboard.writeText(dashboardData.referrer.referral_link);
      toast.success('Referral link copied!');
    }
  };

  const handleSavePaymentMethod = async () => {
    if (!dashboardData?.referrer?.email) return;
    
    setSavingPayment(true);
    try {
      const response = await fetch(`${API_URL}/api/referrals/payment-method`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: dashboardData.referrer.email,
          payment_method: paymentMethod,
          payment_details: paymentDetails
        })
      });
      
      if (response.ok) {
        toast.success('Payment method saved!');
        setShowPaymentSetup(false);
        // Reload dashboard to get updated data
        await loadDashboard(dashboardData.referrer.email);
      } else {
        throw new Error('Failed to save');
      }
    } catch (err) {
      toast.error('Failed to save payment method');
    } finally {
      setSavingPayment(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0b0c] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-[#00d4aa] animate-spin mx-auto mb-4" />
          <p className="text-white/60">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // No referral account
  if (error === 'no_account') {
    return (
      <div className="min-h-screen bg-[#0a0b0c]">
        <div className="bg-[#131416] border-b border-white/10">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-white/70 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
            <img 
              src="https://customer-assets.emergentagent.com/job_tactical-sports/artifacts/00wc7m32_GWINS%20Gear%20Logo.jpg" 
              alt="GWINS" 
              className="h-8"
            />
          </div>
        </div>
        
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <AlertCircle className="h-16 w-16 text-yellow-500 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-white mb-4">No Referral Account Found</h1>
          <p className="text-white/60 mb-8">
            You don't have an approved referral account yet. Apply to join our referral program and start earning 15% commission!
          </p>
          <Button
            onClick={() => navigate('/#referrals')}
            className="bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c] font-bold rounded-full px-8"
          >
            Apply for Referral Program
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0b0c] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-white mb-4">{error}</p>
          <Button onClick={() => window.location.reload()} className="bg-[#00d4aa] text-[#0a0b0c]">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  const stats = dashboardData?.stats || {};
  const referrer = dashboardData?.referrer || {};
  const recentSales = dashboardData?.recent_sales || [];

  return (
    <div className="min-h-screen bg-[#0a0b0c]">
      {/* Header */}
      <div className="bg-[#131416] border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-white/70 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_tactical-sports/artifacts/00wc7m32_GWINS%20Gear%20Logo.jpg" 
              alt="GWINS" 
              className="h-8"
            />
            {user && (
              <div className="flex items-center gap-2">
                {user.picture && (
                  <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                )}
                <span className="text-white/80 text-sm hidden sm:block">{user.name}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-black text-white mb-2">Referral Dashboard</h1>
          <p className="text-white/60">Welcome back, {referrer.name}! Track your earnings and performance.</p>
        </div>

        {/* Referral Link Card */}
        <div className="bg-gradient-to-r from-[#00d4aa]/20 to-[#00d4aa]/5 rounded-2xl p-6 mb-8 border border-[#00d4aa]/30">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <p className="text-white/60 text-sm mb-1">Your Referral Link</p>
              <div className="flex items-center gap-3">
                <code className="text-[#00d4aa] text-lg font-mono">{referrer.referral_link}</code>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleCopyLink}
                className="bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c] font-bold"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy Link
              </Button>
              <Button
                variant="outline"
                className="border-white/20 text-white hover:bg-white/5"
                onClick={() => window.open(referrer.referral_link, '_blank')}
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-4 text-sm">
            <span className="text-white/60">Your Code: <span className="text-white font-bold">{referrer.referral_code}</span></span>
            <span className="text-white/60">Commission Rate: <span className="text-[#00d4aa] font-bold">{stats.commission_rate}%</span></span>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <div className="bg-[#131416] rounded-xl p-5 border border-white/10">
            <div className="flex items-center gap-2 text-white/60 text-sm mb-2">
              <Eye className="h-4 w-4" />
              Link Clicks
            </div>
            <p className="text-3xl font-bold text-white">{stats.total_clicks?.toLocaleString() || 0}</p>
          </div>
          
          <div className="bg-[#131416] rounded-xl p-5 border border-white/10">
            <div className="flex items-center gap-2 text-white/60 text-sm mb-2">
              <ShoppingCart className="h-4 w-4" />
              Orders
            </div>
            <p className="text-3xl font-bold text-white">{stats.total_orders || 0}</p>
          </div>
          
          <div className="bg-[#131416] rounded-xl p-5 border border-white/10">
            <div className="flex items-center gap-2 text-white/60 text-sm mb-2">
              <DollarSign className="h-4 w-4" />
              Total Sales
            </div>
            <p className="text-3xl font-bold text-white">${(stats.total_sales || 0).toFixed(2)}</p>
          </div>
          
          <div className="bg-[#131416] rounded-xl p-5 border border-[#00d4aa]/30">
            <div className="flex items-center gap-2 text-white/60 text-sm mb-2">
              <TrendingUp className="h-4 w-4 text-[#00d4aa]" />
              Earned
            </div>
            <p className="text-3xl font-bold text-[#00d4aa]">${(stats.total_commission || 0).toFixed(2)}</p>
          </div>
          
          <div className="bg-[#131416] rounded-xl p-5 border border-white/10">
            <div className="flex items-center gap-2 text-white/60 text-sm mb-2">
              <Wallet className="h-4 w-4" />
              Paid Out
            </div>
            <p className="text-3xl font-bold text-white">${(stats.paid_out || 0).toFixed(2)}</p>
          </div>
        </div>

        {/* Pending Payout Alert */}
        {stats.pending_payout > 0 && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-8 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock className="h-6 w-6 text-yellow-500" />
              <div>
                <p className="text-white font-semibold">Pending Payout: ${stats.pending_payout.toFixed(2)}</p>
                <p className="text-white/60 text-sm">Payouts are processed monthly on the 1st</p>
              </div>
            </div>
            {!referrer.payment_method && (
              <Button
                onClick={() => setShowPaymentSetup(true)}
                className="bg-yellow-500 hover:bg-yellow-400 text-black font-bold"
              >
                Set Up Payment
              </Button>
            )}
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Recent Sales */}
          <div className="lg:col-span-2">
            <div className="bg-[#131416] rounded-xl border border-white/10 overflow-hidden">
              <div className="p-4 border-b border-white/10">
                <h2 className="text-white font-bold">Recent Sales</h2>
              </div>
              {recentSales.length === 0 ? (
                <div className="p-8 text-center">
                  <ShoppingCart className="h-12 w-12 text-white/20 mx-auto mb-4" />
                  <p className="text-white/60">No sales yet. Share your link to start earning!</p>
                </div>
              ) : (
                <div className="divide-y divide-white/5">
                  {recentSales.map((sale, index) => (
                    <div key={index} className="p-4 flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">Order #{sale.order_id?.slice(-8)}</p>
                        <p className="text-white/50 text-sm flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(sale.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-white">${sale.order_amount?.toFixed(2)}</p>
                        <p className="text-[#00d4aa] text-sm font-semibold">+${sale.commission?.toFixed(2)}</p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        sale.status === 'paid' 
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {sale.status === 'paid' ? 'Paid' : 'Pending'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Payment Method */}
          <div>
            <div className="bg-[#131416] rounded-xl border border-white/10 overflow-hidden">
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <h2 className="text-white font-bold">Payment Method</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPaymentSetup(true)}
                  className="text-[#00d4aa] hover:text-[#33e0bb]"
                >
                  {referrer.payment_method ? 'Edit' : 'Set Up'}
                </Button>
              </div>
              <div className="p-4">
                {referrer.payment_method ? (
                  <div className="flex items-center gap-3">
                    {referrer.payment_method === 'paypal' && (
                      <>
                        <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                          <CreditCard className="h-5 w-5 text-blue-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium">PayPal</p>
                          <p className="text-white/50 text-sm">{referrer.payment_details?.paypal_email}</p>
                        </div>
                      </>
                    )}
                    {referrer.payment_method === 'venmo' && (
                      <>
                        <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                          <CreditCard className="h-5 w-5 text-blue-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium">Venmo</p>
                          <p className="text-white/50 text-sm">{referrer.payment_details?.venmo_handle}</p>
                        </div>
                      </>
                    )}
                    {referrer.payment_method === 'bank' && (
                      <>
                        <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                          <Building className="h-5 w-5 text-green-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium">Bank Transfer</p>
                          <p className="text-white/50 text-sm">{referrer.payment_details?.bank_name}</p>
                        </div>
                      </>
                    )}
                    <CheckCircle className="h-5 w-5 text-green-400 ml-auto" />
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <Wallet className="h-10 w-10 text-white/20 mx-auto mb-3" />
                    <p className="text-white/60 text-sm">No payment method set</p>
                    <Button
                      onClick={() => setShowPaymentSetup(true)}
                      variant="outline"
                      size="sm"
                      className="mt-3 border-[#00d4aa]/30 text-[#00d4aa]"
                    >
                      Add Payment Method
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Tips */}
            <div className="bg-[#131416] rounded-xl border border-white/10 p-4 mt-4">
              <h3 className="text-white font-bold mb-3">Tips to Earn More</h3>
              <ul className="space-y-2 text-white/60 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-[#00d4aa] mt-0.5 flex-shrink-0" />
                  Share your link on social media
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-[#00d4aa] mt-0.5 flex-shrink-0" />
                  Tell your teammates about GWINS
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-[#00d4aa] mt-0.5 flex-shrink-0" />
                  Share at tournaments and events
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-[#00d4aa] mt-0.5 flex-shrink-0" />
                  Create content featuring GWINS gear
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Method Modal */}
      {showPaymentSetup && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-[#131416] rounded-2xl border border-white/10 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10">
              <h2 className="text-xl font-bold text-white">Payment Method Setup</h2>
              <p className="text-white/60 text-sm">Choose how you'd like to receive your payouts</p>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Payment Method Selection */}
              <div className="grid grid-cols-3 gap-3">
                <button
                  onClick={() => setPaymentMethod('paypal')}
                  className={`p-4 rounded-xl border text-center transition-all ${
                    paymentMethod === 'paypal'
                      ? 'border-[#00d4aa] bg-[#00d4aa]/10'
                      : 'border-white/10 hover:border-white/30'
                  }`}
                >
                  <CreditCard className={`h-6 w-6 mx-auto mb-2 ${paymentMethod === 'paypal' ? 'text-[#00d4aa]' : 'text-white/60'}`} />
                  <span className={`text-sm font-medium ${paymentMethod === 'paypal' ? 'text-[#00d4aa]' : 'text-white/60'}`}>PayPal</span>
                </button>
                <button
                  onClick={() => setPaymentMethod('venmo')}
                  className={`p-4 rounded-xl border text-center transition-all ${
                    paymentMethod === 'venmo'
                      ? 'border-[#00d4aa] bg-[#00d4aa]/10'
                      : 'border-white/10 hover:border-white/30'
                  }`}
                >
                  <CreditCard className={`h-6 w-6 mx-auto mb-2 ${paymentMethod === 'venmo' ? 'text-[#00d4aa]' : 'text-white/60'}`} />
                  <span className={`text-sm font-medium ${paymentMethod === 'venmo' ? 'text-[#00d4aa]' : 'text-white/60'}`}>Venmo</span>
                </button>
                <button
                  onClick={() => setPaymentMethod('bank')}
                  className={`p-4 rounded-xl border text-center transition-all ${
                    paymentMethod === 'bank'
                      ? 'border-[#00d4aa] bg-[#00d4aa]/10'
                      : 'border-white/10 hover:border-white/30'
                  }`}
                >
                  <Building className={`h-6 w-6 mx-auto mb-2 ${paymentMethod === 'bank' ? 'text-[#00d4aa]' : 'text-white/60'}`} />
                  <span className={`text-sm font-medium ${paymentMethod === 'bank' ? 'text-[#00d4aa]' : 'text-white/60'}`}>Bank</span>
                </button>
              </div>

              {/* PayPal Details */}
              {paymentMethod === 'paypal' && (
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    PayPal Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                    <Input
                      type="email"
                      value={paymentDetails.paypal_email}
                      onChange={(e) => setPaymentDetails(p => ({ ...p, paypal_email: e.target.value }))}
                      placeholder="your.paypal@email.com"
                      className="pl-10 bg-white/5 border-white/10 text-white"
                    />
                  </div>
                </div>
              )}

              {/* Venmo Details */}
              {paymentMethod === 'venmo' && (
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Venmo Username
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">@</span>
                    <Input
                      type="text"
                      value={paymentDetails.venmo_handle}
                      onChange={(e) => setPaymentDetails(p => ({ ...p, venmo_handle: e.target.value }))}
                      placeholder="yourvenmo"
                      className="pl-8 bg-white/5 border-white/10 text-white"
                    />
                  </div>
                </div>
              )}

              {/* Bank Details */}
              {paymentMethod === 'bank' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">Bank Name</label>
                    <Input
                      type="text"
                      value={paymentDetails.bank_name}
                      onChange={(e) => setPaymentDetails(p => ({ ...p, bank_name: e.target.value }))}
                      placeholder="Chase, Bank of America, etc."
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">Account Holder Name</label>
                    <Input
                      type="text"
                      value={paymentDetails.bank_account_name}
                      onChange={(e) => setPaymentDetails(p => ({ ...p, bank_account_name: e.target.value }))}
                      placeholder="Full name on account"
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">Routing Number</label>
                      <Input
                        type="text"
                        value={paymentDetails.bank_routing}
                        onChange={(e) => setPaymentDetails(p => ({ ...p, bank_routing: e.target.value }))}
                        placeholder="9 digits"
                        className="bg-white/5 border-white/10 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">Account Number</label>
                      <Input
                        type="text"
                        value={paymentDetails.bank_account}
                        onChange={(e) => setPaymentDetails(p => ({ ...p, bank_account: e.target.value }))}
                        placeholder="Account number"
                        className="bg-white/5 border-white/10 text-white"
                      />
                    </div>
                  </div>
                </div>
              )}

              <p className="text-white/40 text-xs">
                Your payment information is securely stored and only used to process your referral payouts.
              </p>
            </div>

            <div className="p-6 border-t border-white/10 flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowPaymentSetup(false)}
                className="flex-1 border-white/20 text-white hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSavePaymentMethod}
                disabled={savingPayment}
                className="flex-1 bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c] font-bold"
              >
                {savingPayment ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                ) : (
                  'Save Payment Method'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralDashboard;
